import React from 'react';
import { createPortal } from 'react-dom';
import { Snackbar, Alert, Box } from '@mui/material';
import type { SnackbarOrigin } from '@mui/material/Snackbar';
import { Z_INDEX } from '../../constants/zIndex';
import { AlertBanner } from './AlertBanner';
import type { AlertBannerSeverity } from './AlertBanner';

export interface CustomSnackbarProps {
  open: boolean;
  onClose: () => void;
  message: React.ReactNode;
  severity: AlertBannerSeverity;
  /**
   * 'alert' = MUI Alert (default). 'banner' = AlertBanner (card-style).
   * Use 'banner' where the app previously used AlertBanner inside Snackbar for consistent look.
   */
  variant?: 'alert' | 'banner';
  anchorOrigin?: SnackbarOrigin;
  autoHideDuration?: number | null;
  /** Extra sx for the Snackbar root */
  sx?: object;
}

const defaultAnchorOrigin: SnackbarOrigin = {
  vertical: 'top',
  horizontal: 'center',
};

/**
 * App-wide snackbar with consistent z-index (above dialogs/menus) and optional
 * top offset for safe area. Use this instead of MUI Snackbar directly.
 */
export const CustomSnackbar: React.FC<CustomSnackbarProps> = ({
  open,
  onClose,
  message,
  severity,
  variant = 'alert',
  anchorOrigin = defaultAnchorOrigin,
  autoHideDuration = null,
  sx = {},
}) => {
  const isTop = anchorOrigin.vertical === 'top';
  // Same positioning as before: below header (80px) + safe area on mobile, 104px on desktop.
  // Applied to root so it's correct when portaled to body (slotProps.root is the positioned element).
  const rootSx = {
    zIndex: Z_INDEX.SNACKBAR,
    position: 'fixed' as const,
    ...(isTop && {
      top: { xs: 'calc(80px + env(safe-area-inset-top, 0px))', sm: 104 },
    }),
    ...(anchorOrigin.vertical !== 'top' && {
      bottom: { xs: 8, sm: 24 },
    }),
    ...sx,
  };

  const snackbarContent = (
    <Snackbar
      open={open}
      onClose={onClose}
      anchorOrigin={anchorOrigin}
      autoHideDuration={autoHideDuration}
      sx={rootSx}
      slotProps={{ root: { sx: rootSx } }}
    >
      {variant === 'banner' ? (
        <Box sx={{ width: '100%' }}>
          <AlertBanner
            severity={severity}
            message={message}
            sx={{ mb: 0 }}
          />
        </Box>
      ) : (
        <Alert onClose={onClose} severity={severity} sx={{ width: '100%' }}>
          {message}
        </Alert>
      )}
    </Snackbar>
  );

  // Portal to document.body so the snackbar is a sibling of modal portals and
  // can stack above dialogs (z-index 10000 > dialog 310). Without this, the
  // snackbar would stay inside the app root and appear behind open modals.
  return createPortal(snackbarContent, document.body);
};
